//Main screen rendered when user wants to see myAccount Info containing
// all the information regarding organisation.
import React, { useEffect, useState } from "react";
// Styles
import Styles from "./myAccount.module.scss";
import { ACCENTCOLOR, BLACKCOLOR } from "../../../Shared/colors";

// components
import PocEdit from "./poc-edit/pocEdit";
import Loading from "../../../Shared/loading/loading";
import Toast from "../../../Shared/toast/toast";
import TopBar from "../../../Shared/top-bar/TopBar";

// Functions
import { callApi } from "../../../api/fetch";
import {
  hasAccountManager,
  hasBusinessManager,
  isApiNotFoundResponse,
  normalizeOrganisationManagers,
  parseApiJsonField,
} from "../../../utils/dashboardMappers";
import { testIdProps } from "../../../utils/testIdProps";

// Constants
import { Organisation, Poc } from "../../../constants/org";
import noKamAssignedIllustration from "../../../assets/images/noAccountManagerAssigned.svg";
import noBmAssignedIllustration from "../../../assets/images/noBusinessManagerAssigned.svg";
import DarkLogoSVG from "../../../Shared/svg/darkLogoSVG";
import ArrowUp from "../../../Shared/svg/arrowUp";

// Hooks
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../../Shared/useTitle";
import { getCurrentOrganisationId } from "../../../utils/authSession";

function MyAccount() {
  const history = useNavigate();
  const [toggleTechPOCModal, setToggleTechPOCModal] = useState(false);
  const [toggleBusinessPOCModal, setToggleBusinessPOCModal] =
    useState(false);
  const [org, setOrg] = useState<null | Organisation>();
  const [kycDetails, setKycDetails] = useState({
    gstin: "",
  });
  const [fullPageLoad, setFullPageLoad] = useState(false);
  const [editTechPocCardLoading, setEditTechPocCardLoading] =
    useState(false);
  const [editBusinessPocCardLoading, setEditBusinessPocCardLoading] =
    useState(false);

  const kamEmptyState = (
    <div
      style={{ height: "150px" }}
      className="d-flex align-items-center justify-content-center"
    >
      <div className="text-center">
        <img
          src={noKamAssignedIllustration}
          alt="no_kam_assigned"
          style={{ width: "100px" }}
        />
        <h6 className="mb-0 pt-3 px-2">No Kam Assigned</h6>
      </div>
    </div>
  );

  const BmEmptyState = (
    <div
      style={{ height: "150px" }}
      className="d-flex align-items-center justify-content-center"
    >
      <div className="text-center">
        <img
          src={noBmAssignedIllustration}
          alt="no_bm_assigned"
          style={{ width: "100px" }}
        />
        <h6 className="mb-0 pt-3 px-2">No BM Assigned</h6>
      </div>
    </div>
  );

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }
  useEffect(() => {
    async function getOrgDetails() {
      const orgId = getCurrentOrganisationId();
      if (!orgId) return;

      setFullPageLoad(true);
      try {
        const data = await callApi(`/organisation`, {
          method: "GET",
        });
        if (isApiNotFoundResponse(data)) {
          throw new Error("Organisation not found");
        }

        const orgData = normalizeOrganisationManagers(data as Organisation);
        const businessPoc = parseApiJsonField(orgData.business_poc, undefined);
        const technicalPoc = parseApiJsonField(orgData.technical_poc, undefined);
        const kyc = parseApiJsonField(orgData.kyc, { gstin: "" });

        setOrg({
          ...orgData,
          business_poc: businessPoc,
          technical_poc: technicalPoc,
          kyc,
        });
        setKycDetails({ gstin: kyc.gstin ?? "" });
      } catch (err) {
        toastTimeout(true, "error", (err as Error).message);
      } finally {
        setFullPageLoad(false);
      }
    }
    getOrgDetails();
  }, []);
  async function handleTechPOCEdit(value: Poc) {
    const orgId = getCurrentOrganisationId();
    if (!orgId) return;

    setToggleTechPOCModal(false);
    setEditTechPocCardLoading(true);
    try {
      const data = await callApi(`/organisation`, {
        method: "PUT",
        data: {
          technical_poc: value,
        },
      });
      if (isApiNotFoundResponse(data)) {
        throw new Error("Organisation not found");
      }

      setOrg((org: any) => ({
        ...org,
        technical_poc: parseApiJsonField(
          (data as Organisation).technical_poc,
          value
        ),
      }));
    } catch (err) {
      toastTimeout(true, "error", (err as Error).message);
    } finally {
      setEditTechPocCardLoading(false);
    }
    return;
  }
  async function handleBusinessPOCEdit(value: Poc) {
    const orgId = getCurrentOrganisationId();
    if (!orgId) return;

    setToggleBusinessPOCModal(false);
    setEditBusinessPocCardLoading(true);
    try {
      const data = await callApi(`/organisation`, {
        method: "PUT",
        data: {
          business_poc: value,
        },
      });
      if (isApiNotFoundResponse(data)) {
        throw new Error("Organisation not found");
      }

      setOrg((org: any) => ({
        ...org,
        business_poc: parseApiJsonField(
          (data as Organisation).business_poc,
          value
        ),
      }));
    } catch (err) {
      toastTimeout(true, "error", (err as Error).message);
    } finally {
      setEditBusinessPocCardLoading(false);
    }
    return;
  }
  function handleTechPocEditCancel() {
    setToggleTechPOCModal(false);
    return;
  }
  function handleBusinessPocEditCancel() {
    setToggleBusinessPOCModal(false);
    return;
  }
  useTitle("Zoop Customer Platform | My Account Info");
  return (
    <div style={{ background: "#F3F4F6" }}>
      <div className={`${Styles.background}`}>
        <div className="d-flex align-items-center justify-content-between px-4 ">
          <DarkLogoSVG />
          <TopBar />
        </div>
        {fullPageLoad ? (
          <div style={{ height: "calc(100vh - 70px)" }}>
            <Loading loadingColor={ACCENTCOLOR} />
          </div>
        ) : (
          <div className={"container-fluid px-4"}>
            <div className={"p-3"}>
              <div className="d-flex align-items-center py-3">
                <div
                  className="d-flex align-items-center"
                  {...testIdProps("my-account-back")}
                  onClick={() => history("/application")}
                  style={{ cursor: "pointer" }}
                >
                  <div className={`px-1 ${Styles.rotate}`}>
                    <ArrowUp color={BLACKCOLOR} />
                  </div>
                  <p className={"mb-0 px-2 heading_text"}>
                    Business Information
                  </p>
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-xl-12 p-3">
                  <div className={`${Styles.card} ${Styles.card2Animation}`}>
                    <div className={`p-3 ${Styles.cardHeader}`}>
                      <p className={"mb-0 px-2 card_title_bold_text"}>
                        Business Information
                      </p>
                    </div>
                    <div className="p-3">
                      <div className="d-flex align-items-start py-2 my-1">
                        <div className={`${Styles.keyWidth} px-2`}>
                          <p className={`mb-0 ${Styles.key}`}>Business Name:</p>
                        </div>
                        <div className="px-2">
                          <p className={`mb-0 ${Styles.value}`}>{org?.org_name}</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-start py-2 my-1">
                        <div className={`${Styles.keyWidth} px-2`}>
                          <p className={`mb-0 ${Styles.key}`}>Category:</p>
                        </div>
                        <div className="px-2">
                          <p className={`mb-0 ${Styles.value}`}>
                            {org?.category}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-start py-2 my-1">
                        <div className={`${Styles.keyWidth} px-2`}>
                          <p className={`mb-0 ${Styles.key}`}>Country Code:</p>
                        </div>
                        <div className="px-2">
                          <p className={`mb-0 ${Styles.value}`}>
                            {org?.country_code}
                          </p>
                        </div>
                      </div>
                      {/* <div className="d-flex align-items-start py-2 my-1">
                        <div className={`${Styles.keyWidth} px-2`}>
                          <p className={`mb-0 ${Styles.key}`}>
                            Company Address:
                          </p>
                        </div>
                        <div className="px-2">
                          <p className={`mb-0 ${Styles.value}`}>
                            Creaticity Mall, Opposite Golf Course,
                          </p>
                          <p className={`mb-0 ${Styles.value}`}>
                            Shastrinagar, Yerawada, Pune,
                          </p>
                          <p className={`mb-0 ${Styles.value}`}>
                            Maharashtra 411006
                          </p>
                        </div>
                      </div> */}
                      {kycDetails?.gstin && (
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>GSTIN:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {kycDetails.gstin ?? "NA"}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="d-flex align-items-start py-2 my-1">
                        <div className={`${Styles.keyWidth} px-2`}>
                          <p className={`mb-0 ${Styles.key}`}>Trade Name:</p>
                        </div>
                        <div className="px-2">
                          <p className={`mb-0 ${Styles.value}`}>
                            {org?.org_trade_name || "NA"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-xl-6 p-3">
                  <div className={`${Styles.card} ${Styles.card1Animation}`}>
                    <div className={`p-3 ${Styles.cardHeader}`}>
                      <p className={"mb-0 px-2 card_title_bold_text"}>
                        Account Manager
                      </p>
                    </div>
                    <div className="p-3">
                      {hasAccountManager(org?.kam) ? (
                        console.log("accountManager: ", org?.kam),
                        <div>
                          {org?.kam?.kam_name ||
                            org?.kam?.kam_email ||
                            org?.kam?.kam_phone ? (
                            <>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Name:</p>
                                </div>
                                <div className="px-2">
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.kam?.kam_name || "—"}
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Email:</p>
                                </div>
                                <div className="px-2">
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.kam?.kam_email || "—"}
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Contact:</p>
                                </div>
                                <div className={`${Styles.valueWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.kam?.kam_phone || "—"}
                                  </p>
                                </div>
                              </div>
                            </>
                          ) : (
                            <p className={`mb-0 px-2 ${Styles.value}`}>
                              Account manager assigned to your organisation.
                            </p>
                          )}
                        </div>
                      ) : (
                        kamEmptyState
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-6 p-3">
                  <div className={`${Styles.card} ${Styles.card1Animation}`}>
                    <div className={`p-3 ${Styles.cardHeader}`}>
                      <p className={"mb-0 px-2  card_title_bold_text"}>
                        Business Manager
                      </p>
                    </div>
                    <div className="p-3">
                      {hasBusinessManager(org?.bm) ? (
                        <div>
                          {org?.bm?.bm_name ||
                            org?.bm?.bm_email ||
                            org?.bm?.bm_phone ? (
                            <>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Name:</p>
                                </div>
                                <div className="px-2">
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.bm?.bm_name || "—"}
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Email:</p>
                                </div>
                                <div className="px-2">
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.bm?.bm_email || "—"}
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-start py-2 my-1">
                                <div className={`${Styles.keyWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.key}`}>Contact:</p>
                                </div>
                                <div className={`${Styles.valueWidth} px-2`}>
                                  <p className={`mb-0 ${Styles.value}`}>
                                    {org?.bm?.bm_phone || "—"}
                                  </p>
                                </div>
                              </div>
                            </>
                          ) : (
                            <p className={`mb-0 px-2 ${Styles.value}`}>
                              Business manager assigned to your organisation.
                            </p>
                          )}
                        </div>
                      ) : (
                        BmEmptyState
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-xl-6 p-3">
                  {editTechPocCardLoading ? (
                    <div
                      className={`${Styles.loadingCard} d-flex align-items-center justify-content-center`}
                    >
                      <Loading loadingColor={ACCENTCOLOR} />
                    </div>
                  ) : (
                    <div className={`${Styles.card} ${Styles.card3Animation}`}>
                      <div className={`p-3 ${Styles.cardHeader}`}>
                        <div className="d-flex align-items-center">
                          <p className={"mb-0 px-2  card_title_bold_text"}>
                            Technical Owner Details
                          </p>
                          <div className="ml-auto px-2">
                            <p
                              className={`${Styles.editTextStyle} mb-0`}
                              {...testIdProps("my-account-edit-technical-owner")}
                              onClick={() => setToggleTechPOCModal(true)}
                            >
                              Edit
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Name:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.technical_poc?.name}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Email:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.technical_poc?.email}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Contact:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.technical_poc?.phone}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Designation:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.technical_poc?.designation}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-xl-6 p-3">
                  {editBusinessPocCardLoading ? (
                    <div
                      className={`${Styles.loadingCard} d-flex align-items-center justify-content-center`}
                    >
                      <Loading loadingColor={ACCENTCOLOR} />
                    </div>
                  ) : (
                    <div className={`${Styles.card} ${Styles.card4Animation}`}>
                      <div className={`p-3 ${Styles.cardHeader}`}>
                        <div className="d-flex align-items-center">
                          <p className={"mb-0 px-2  card_title_bold_text"}>
                            Business Owner Details
                          </p>
                          <div className="ml-auto px-2">
                            <p
                              className={`${Styles.editTextStyle} mb-0`}
                              {...testIdProps("my-account-edit-business-owner")}
                              onClick={() => setToggleBusinessPOCModal(true)}
                            >
                              Edit
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Name:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.business_poc?.name}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Email:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.business_poc?.email}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Contact:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.business_poc?.phone}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-start py-2 my-1">
                          <div className={`${Styles.keyWidth} px-2`}>
                            <p className={`mb-0 ${Styles.key}`}>Designation:</p>
                          </div>
                          <div className="px-2">
                            <p className={`mb-0 ${Styles.value}`}>
                              {org?.business_poc?.designation}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {toggleTechPOCModal ? (
        <PocEdit
          pocInfo={org?.technical_poc}
          orgCountryCode={org?.country_code}
          onSubmit={(value: any) => handleTechPOCEdit(value)}
          onCancel={handleTechPocEditCancel}
        />
      ) : null}
      {toggleBusinessPOCModal ? (
        <PocEdit
          pocInfo={org?.business_poc}
          orgCountryCode={org?.country_code}
          onSubmit={(value: any) => handleBusinessPOCEdit(value)}
          onCancel={handleBusinessPocEditCancel}
        />
      ) : null}
    </div>
  );
}

export default MyAccount;
